/**
 * P3-PROPOSAL — Customer spec sheet contract (deliverables 1, 2, 7-9).
 *
 * Single-page A4 spec sheet ("Your Custom Ski Design") rendered from the
 * APPROVED design version's RenderFacet + recommendation state — never
 * re-derived, never invented. The binding element->source mapping lives in
 * docs/reference/spec-sheet-analysis.md §2; this module encodes it as a
 * typed, versioned, testable data contract.
 *
 * Laws (GOV-GATES #3/#4/#5/#6):
 * - Every displayed number originates from the versioned record (facet /
 *   design state). A missing source is an explicit TBD state, never a guess.
 * - No invented domain facts: narrative words like the tip "shape" adjective
 *   come from the record or are omitted — never synthesized.
 * - Determinism: same version + inputs -> byte-identical sheet data.
 * - Graphics status is explicit: pending until a Konstner proof exists.
 */
import { fillablePlanViewPath, pointOnArc, type RenderArc, type RenderFacet } from './renderFacet';
import type { DesignState } from './designVersioning';
import type { AnchorsSection } from './schemas';

export const SPEC_SHEET_VERSION = '0.1';

/** Explicit per-field source state — a number is either sourced or TBD. */
export type SourcedNumber =
  | { kind: 'sourced'; value: number; source: string }
  | { kind: 'tbd'; reason: string };

export interface SpecSheetCustomer {
  /** "Handcrafted for <name>" rail text + header. */
  name: string;
  /** Displayed serial — TBD when the design has none yet. */
  serial: SourcedNumber;
}

export interface SpecSheetDimensions {
  /** LENGTH rule (cm) — from state.length. */
  lengthCm: SourcedNumber;
  /** Tip width arrow (mm) — from state.noseWidth. */
  tipWidthMm: SourcedNumber;
  /** Waist width arrow (mm) — from state.waistWidthRecommendation. */
  waistWidthMm: SourcedNumber;
  /** Tail width arrow (mm) — from state.tailWidth. */
  tailWidthMm: SourcedNumber;
  /** RADIUS leader (m) — from the precedent record when it carries one. */
  radiusM: SourcedNumber;
  /** Plan-view outline — the SAME arc serialization the workspace renders. */
  planViewSvg: string;
}

export interface SpecSheetProfile {
  /** Side-profile curve SVG (tip/tail arcs + camber bow from the facet). */
  profileSvg: string;
  /** ROCKER / CAMBER / ROCKER range-bar labels, e.g. "Rocker 30-10". */
  tipRockerLabel: string;
  tailRockerLabel: string;
  camberLabel: string;
  /** Pete 2026-10-28: customer-facing camber description — the CAMBER: line
   *  under TAIL: in the PROFILE section. */
  camberDescription: string;
  /** Signed block shift toward the waist, mm — negative reduces the rocker
   *  zone (Pete 2026-10-26: "a negative Nose Inward reduces Tip Rocker run
   *  length"). Absent = no adjustment. Passes through from the order form's
   *  noseInwardAdjustment / tailInwardAdjustment. */
  noseInwardAdjustmentMm?: number;
  tailInwardAdjustmentMm?: number;
  /** Raw camber enum (-1 Reverse, 0 Flat/None, 1 Low, 2 Medium, 3 High) —
   *  drives the PDF sideview's per-camber display geometry (Pete 2026-10-23). */
  camberEnum?: string;
  /** Narrative lines — every word from the record, none synthesized. */
  tipNarrative: string;
  tailNarrative: string;
  /** Tip / tail shape descriptions (orderForm.tipDescription / tailDescription). */
  tipDescription: string;
  tailDescription: string;
  /** Camber bow carries a truthful mm height only when the mapping knows it. */
  camberHeightMm: SourcedNumber;
  /** Core thickness profile (mm) + taper shape — the thickness sideview outline. */
  coreThickness: {
    noseMm: SourcedNumber;
    waistMm: SourcedNumber;
    tailMm: SourcedNumber;
    tipTaper: string;
    tailTaper: string;
  };
}

export interface SpecSheetConstruction {
  /** "Poplar-Paulownia wood core with Carbon 2x2 twill" style line. */
  headline: string;
  /** Bulleted materials list (core, structural, base, aramid, topsheet). */
  bullets: string[];
  /** Laminate illustration asset key — per-construction static asset (plan §4.4). */
  laminateAssetKey: string;
  /** Flex pattern (orderForm.flexPattern). */
  flexPattern: string;
  /** Overall stiffness name (orderForm.overallStiffness.name). */
  stiffness: string;
  /** Ordered materials stack for the laminate illustration labels. */
  materialsStack: string[];
  /**
   * Bindings name (Sales-tab Binding Type dropdown -> orderForm.bindingType.name).
   * null = hidden (NONE / OTHER bindings never render — Pete 2026-10-27).
   */
  bindings: string | null;
  /** Top Graphic description (orderForm.topGraphic) — null when the order
   *  carries none. Pete 2026-10-28: GRAPHIC is another line in YOUR BUILD. */
  graphic: string | null;
  /**
   * Package upgrades block (Pete 2026-10-30): when the order carries an
   * upgrade bundle (Silver Package / Ultra Package / Factory Flow Bundle),
   * YOUR BUILD gains a header line ('SILVER PACKAGE UPGRADES:' etc., under
   * STIFFNESS, before BINDINGS) followed by the bundle's material upgrades.
   * Included per bundle (Pete): Base Material in all three; Construction
   * (Titanal or CarbonWrap) in Silver + Ultra; Aramid in all three;
   * Calibrated Visco Dampening in Ultra only. null/absent = no block.
   */
  packageUpgrades: {
    header: string;
    /** Benefit sentences in order — NO per-upgrade labels (Pete 2026-10-30). */
    lines: string[];
  } | null;
  /** Pete 2026-11-03: MATERIAL LAYUP section - the layer stack, TOP first. */
  materialLayup: string[];
}

export interface SpecSheetGraphics {
  /** NEVER implies finality until a Konstner proof exists. 'catalog' =
   *  pre-designed graphic (House Graphic / Niehues) — no proof cycle, only a
   *  Konstner placement pass on the shapefile. */
  status: 'pending' | 'in-review' | 'approved' | 'catalog';
  /** Step-5 bold caps label — tier-aware (Pete 2026-10-28). */
  label: string;
  description: string;
  /** Clickable URLs included in the description (become link annotations). */
  urls: string[];
}

export interface SpecSheetData {
  sheetVersion: string;
  /** The approved design version this sheet renders — the audit anchor. */
  designVersion: number;
  customer: SpecSheetCustomer;
  dimensions: SpecSheetDimensions;
  profile: SpecSheetProfile;
  construction: SpecSheetConstruction;
  graphics: SpecSheetGraphics;
  /** Footer contact block — canonical text, business-confirmed. */
  footerLine: string;
  /** Pete 2026-10-28: HOW THEY'LL RIDE — the Sales Recommendation paragraph
   *  from the bottom of the Ski Proposal. Empty string hides the section. */
  designSummary: string;
  /** Pete 2026-10-28: PRICING — link to the itemized Shopify draft invoice. */
  pricing: { invoiceUrl: string | null };
  /** Narrative framing draft (deliverable 3): designer edits + approves. */
  narrative: AnchorsSection;
  /** Provenance audit: every rendered field -> where it came from. */
  provenance: Array<{ field: string; source: string }>;
}

export interface SpecSheetInput {
  /** The APPROVED design version number (from the version log). */
  designVersion: number;
  customerName: string;
  serial?: number;
  state: DesignState;
  facet: RenderFacet;
  /** Precedent-sourced sidecut radius (m) when the record carries one. */
  sidecutRadiusM?: number;
  construction?: {
    coreDescription?: string;
    structuralDescription?: string;
    baseDescription?: string;
    aramid?: string;
    topsheetDescription?: string;
    /** Flex pattern name (orderForm.flexPattern). */
    flexPattern?: string;
    /** Overall stiffness name (orderForm.overallStiffness.name). */
    stiffness?: string;
    /** Ordered materials stack labels for the laminate illustration. */
    materialsStack?: string[];
    /** Bindings name (Sales-tab Binding Type dropdown -> orderForm.bindingType.name). */
    bindings?: string;
    /** Top Graphic description (orderForm.topGraphic) — Pete 2026-10-28:
     *  GRAPHIC is another line in YOUR BUILD, styled like BINDINGS. */
    graphic?: string;
    /** Package upgrades block — see SpecSheetConstruction.packageUpgrades
     *  (Pete 2026-10-30). Absent = the order carries no upgrade bundle. */
    packageUpgrades?: {
      header: string;
      lines: string[];
    };
    /** Pete 2026-11-03: MATERIAL LAYUP - the layer stack, TOP first
     *  (materialLayupFor in orderProposalSheet). */
    materialLayup?: string[];
  };
  /** Core thickness profile (orderForm.coreThickness*) — the thickness sideview. */
  coreThickness?: {
    noseMm?: number;
    waistMm?: number;
    tailMm?: number;
    tipTaper?: string;
    tailTaper?: string;
  };
  /** Tip / tail shape descriptions (orderForm.tipDescription / tailDescription). */
  tipDescription?: string;
  tailDescription?: string;
  graphicsDescription?: string;
  /** Clickable URLs in the graphics description (link annotations). */
  graphicsUrls?: string[];
  /** Konstner proof state — default pending; 'catalog' for pre-designed art. */
  graphicsStatus?: SpecSheetGraphics['status'];
  /** Pete 2026-10-28: Sales Recommendation paragraph (bottom of the Ski
   *  Proposal) — rendered as the HOW THEY'LL RIDE section. Empty/omitted =
   *  section hidden. */
  designSummary?: string;
  /** Pete 2026-10-28: Shopify draft-order invoice URL (the customer's
   *  itemized invoice / checkout link). Absent = PRICING section hidden. */
  pricingInvoiceUrl?: string;
  /** Step-5 tier: 'custom' (design cycle, default) or 'catalog' (pre-designed
   *  House Graphic / Niehues art — no proof iterations expected). */
  graphicsTier?: 'custom' | 'catalog';
  /** Optional explicit Step-5 label override (e.g. 'NIEHUES:'). */
  graphicsLabel?: string;
  anchors: AnchorsSection;
}

const TBD_SIDECUT =
  'sidecut radius not carried on the engine record — sourced from precedent when present, otherwise TBD';

function num(n: number | undefined, source: string, tbdReason: string): SourcedNumber {
  // Zero is a legitimate sourced value (flat camber = 0mm per Pete's mapping);
  // only missing/invalid (NaN, negative) numbers are TBD. NEGATIVE is also
  // legitimate for REVERSE camber (signed center height, profileCurve.ts
  // CAMBER_HEIGHTS_MM['-1'] = -5mm) — reject only non-finite.
  if (n !== undefined && Number.isFinite(n)) {
    return { kind: 'sourced', value: n, source };
  }
  return { kind: 'tbd', reason: tbdReason };
}

function fmt(n: number, digits = 1): string {
  return n.toFixed(digits);
}

/**
 * Build the typed sheet data from the versioned record. Pure + deterministic.
 * `serial` is TBD when absent — a proposal for an unbuilt design has no serial.
 */
export function buildSpecSheet(input: SpecSheetInput): SpecSheetData {
  const f = input.facet;
  const st = input.state;

  const lengthCm = num(st.length, 'design-state.length (cm)', 'no length on record');
  const tipWidth = num(st.noseWidth, 'design-state.noseWidth (mm)', 'no nose width on record');
  const waistWidth = num(st.waistWidthRecommendation, 'design-state.waistWidthRecommendation (mm)', 'no waist width on record');
  const tailWidth = num(st.tailWidth, 'design-state.tailWidth (mm)', 'no tail width on record');
  const radius = num(input.sidecutRadiusM, 'precedent record sidecutRadius (m)', TBD_SIDECUT);
  const camberHeight = num(
    f.profile.camberHeightMm,
    'profile-curve camber mapping (Pete 2026-09-25)',
    'camber height not on record',
  );

  const tipRockerName = f.profile.tipRockerName;
  const tailRockerName = f.profile.tailRockerName;
  const camberLabel = camberLabelFor(f.profile.camberEnum);

  const bullets: string[] = [];
  const c = input.construction ?? {};
  if (c.coreDescription) bullets.push(`Core: ${c.coreDescription}`);
  if (c.structuralDescription) bullets.push(`Structural layer: ${c.structuralDescription}`);
  if (c.baseDescription) bullets.push(`Base: ${c.baseDescription}`);
  if (c.aramid) bullets.push(`Aramid: ${c.aramid}`);
  if (c.topsheetDescription) bullets.push(`Topsheet: ${c.topsheetDescription}`);

  // Step-5 tier-awareness (Pete 2026-10-28): a catalog pick (House Graphic /
  // Niehues) must not read as "design in progress" — the art already exists;
  // it only needs Konstner placement on the shapefile. Label follows tier.
  const graphicsTier = input.graphicsTier ?? 'custom';
  const graphicsStatus: SpecSheetGraphics['status'] =
    input.graphicsStatus ?? (graphicsTier === 'catalog' ? 'catalog' : 'pending');
  const graphicsDesc = input.graphicsDescription
    ?? (graphicsTier === 'catalog'
      ? 'Pre-designed graphic from our collection — placed on your ski by our graphics team.'
      : 'Custom topsheet graphic — design in progress with our graphics team.');
  const graphicsLabel = input.graphicsLabel ?? (graphicsTier === 'catalog' ? 'HOUSE GRAPHIC:' : 'CUSTOM:');

  const provenance: Array<{ field: string; source: string }> = [
    { field: 'customer.name', source: 'session input (sales rep entry)' },
    { field: 'customer.serial', source: serialSource(input.serial) },
    { field: 'dimensions.lengthCm', source: sourceOf(lengthCm) },
    { field: 'dimensions.tipWidthMm', source: sourceOf(tipWidth) },
    { field: 'dimensions.waistWidthMm', source: sourceOf(waistWidth) },
    { field: 'dimensions.tailWidthMm', source: sourceOf(tailWidth) },
    { field: 'dimensions.radiusM', source: sourceOf(radius) },
    { field: 'dimensions.planViewSvg', source: 'renderFacet.planViewOutlinePath — same arcs as workspace' },
    { field: 'profile.profileSvg', source: 'renderFacet.profile (block arcs + camber mapping)' },
    { field: 'profile.tipRockerLabel', source: 'renderFacet.profile.tipRockerName' },
    { field: 'profile.tailRockerLabel', source: 'renderFacet.profile.tailRockerName' },
    { field: 'profile.camberLabel', source: `renderFacet.profile.camberEnum (${f.profile.camberEnum})` },
    { field: 'profile.camberEnum', source: `renderFacet.profile.camberEnum (${f.profile.camberEnum})` },
    { field: 'profile.camberHeightMm', source: sourceOf(camberHeight) },
    { field: 'construction.headline', source: 'construction descriptions (engine construction selection)' },
    { field: 'construction.bindings', source: 'Sales-tab Binding Type dropdown (orderForm.bindingType.name); NONE/OTHER hidden' },
    { field: 'construction.graphic', source: 'orderForm.topGraphic (Top Graphic description)' },
    { field: 'graphics.status', source: `graphics tier (${graphicsTier}) -> ${graphicsStatus}` },
    { field: 'graphics.label', source: `Step-5 label from tier (${graphicsLabel})` },
    { field: 'designSummary', source: 'Sales Recommendation paragraph (bottom of the Ski Proposal)' },
    { field: 'pricing.invoiceUrl', source: 'Shopify draft-order invoice_url (draft-order child route)' },
    { field: 'narrative', source: 'anchorStatements (D4) — designer-approved' },
  ];

  return {
    sheetVersion: SPEC_SHEET_VERSION,
    designVersion: input.designVersion,
    customer: {
      name: input.customerName,
      serial: input.serial !== undefined && Number.isFinite(input.serial)
        ? { kind: 'sourced', value: input.serial, source: 'order.serialNum' }
        : { kind: 'tbd', reason: 'design not yet built — serial assigned at production' },
    },
    dimensions: {
      lengthCm,
      tipWidthMm: tipWidth,
      waistWidthMm: waistWidth,
      tailWidthMm: tailWidth,
      radiusM: radius,
      planViewSvg: planViewOutline(f),
    },
    profile: {
      profileSvg: sideProfileSvg(f),
      tipRockerLabel: tipRockerName ? `Rocker ${tipRockerName}` : 'Rocker TBD',
      tailRockerLabel: tailRockerName ? `Rocker ${tailRockerName}` : 'Rocker TBD',
      camberLabel,
      camberDescription: camberDescriptionFor(f.profile.camberEnum),
      // Pete 2026-10-26: Nose/Tail Inward slide the end blocks toward the
      // waist; the (negative) adjustment shortens the drawn rocker zone.
      // Pass through from the order form when present.
      noseInwardAdjustmentMm: f.profile.noseInwardAdjustmentMm,
      tailInwardAdjustmentMm: f.profile.tailInwardAdjustmentMm,
      camberEnum: String(f.profile.camberEnum),
      tipNarrative: tipRockerName
        ? `Tip with ${tipRockerName} rocker for easy turn initiation and good flotation`
        : '',
      tailNarrative: tailRockerName
        ? `Tail with ${tailRockerName} rocker for easy turn release and good flotation`
        : '',
      tipDescription: input.tipDescription ?? '',
      tailDescription: input.tailDescription ?? '',
      camberHeightMm: camberHeight,
      coreThickness: {
        noseMm: num(input.coreThickness?.noseMm, 'orderForm.coreThicknessNose (mm)', 'core thickness (nose) not on record'),
        waistMm: num(input.coreThickness?.waistMm, 'orderForm.coreThicknessWaist (mm)', 'core thickness (waist) not on record'),
        tailMm: num(input.coreThickness?.tailMm, 'orderForm.coreThicknessTail (mm)', 'core thickness (tail) not on record'),
        tipTaper: input.coreThickness?.tipTaper ?? '',
        tailTaper: input.coreThickness?.tailTaper ?? '',
      },
    },
    construction: {
      // Pete 2026-10-30: the Aramid toughness sentence is REMOVED from the
      // CONSTRUCTION headline — Aramid now lists under the PACKAGE UPGRADES
      // block (all three bundles), so the CONSTRUCTION line carries core +
      // structural only (2026-10-28 append superseded).
      headline: [
        c.coreDescription && c.structuralDescription
          ? `${c.coreDescription} with ${c.structuralDescription}`
          : (c.coreDescription ?? c.structuralDescription ?? 'Construction details at design finalization'),
      ].join(' '),
      bullets,
      laminateAssetKey: laminateKey(c),
      flexPattern: c.flexPattern ?? '',
      stiffness: c.stiffness ?? '',
      materialsStack: c.materialsStack ?? [],
      // Pete 2026-10-27: NONE or OTHER bindings -> the line never renders.
      bindings: c.bindings && !/^(NONE|OTHER)\b/i.test(c.bindings.trim())
        ? c.bindings.trim()
        : null,
      // Pete 2026-10-28: the Top Graphic description — just another YOUR BUILD
      // line; null hides the line (no graphic picked yet).
      graphic: c.graphic?.trim() || null,
      // Pete 2026-10-30: package upgrades block (Silver/Ultra/Flow bundles).
      packageUpgrades: c.packageUpgrades?.header
        ? { header: c.packageUpgrades.header, lines: c.packageUpgrades.lines }
        : null,
      // Pete 2026-11-03: MATERIAL LAYUP - the real layer stack, TOP first.
      materialLayup: c.materialLayup ?? [],
    },
    graphics: { status: graphicsStatus, label: graphicsLabel, description: graphicsDesc, urls: input.graphicsUrls ?? [] },
    footerLine: 'Wagner Custom Skis - handcrafted in Telluride, Colorado - wagnerskis.com',
    designSummary: input.designSummary?.trim() ?? '',
    pricing: { invoiceUrl: input.pricingInvoiceUrl?.trim() || null },
    narrative: input.anchors,
    provenance,
  };
}

function serialSource(serial: number | undefined): string {
  return serial !== undefined && Number.isFinite(serial)
    ? 'order.serialNum'
    : 'TBD — not yet built';
}

function sourceOf(n: SourcedNumber): string {
  return n.kind === 'sourced' ? n.source : `TBD — ${n.reason}`;
}

/**
 * Pete 2026-10-28 (simplified ruling): the PROFILE section's CAMBER: line is
 * just the name - "Reverse Camber" / "Zero Camber" / "Low Camber" /
 * "Medium Camber" / "High Camber" - no sentences.
 */
export function camberDescriptionFor(camberEnum: string): string {
  // Pete 2026-10-28 (verbatim rulings): Reverse is Pete's own line; Zero /
  // Low / Medium / High are the approved recommendations ("all look good").
  switch (String(camberEnum)) {
    case '-1': return 'Reverse camber - the running surface bows away from the snow for a surfy, playful feel and easy turn release';
    case '0': return 'Zero camber - a flat running surface for even, predictable edge contact and a stable, neutral feel.';
    case '1': return 'Low camber - gentle underfoot arc for easy turn release with dependable edge grip.';
    case '2': return 'Medium camber - a lively underfoot arc for energetic turns and confident edge grip on firm snow.';
    case '3': return 'High camber - a powerful underfoot arc for maximum energy, pop, and grip on hard snow.';
    default: return 'Camber TBD';
  }
}

function camberLabelFor(camberEnum: string): string {
  switch (String(camberEnum)) {
    case '-1': return 'Reverse Camber';
    case '0': return 'Zero Camber';
    case '1': return 'Low Camber';
    case '2': return 'Medium Camber';
    case '3': return 'High Camber';
    default: return 'Camber TBD';
  }
}

function laminateKey(c: SpecSheetInput['construction']): string {
  const core = c?.coreDescription ? slug(c.coreDescription) : 'default';
  return `laminate-stack-${core}`;
}

function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

/**
 * Plan-view outline SVG — 1:1 the same arc-to-path serialization the
 * workspace renders (renderFacet.planViewOutlinePath); the sheet never
 * re-derives geometry (spec analysis §4.3). Fill #3D9CC1 per reference.
 */
function planViewOutline(f: RenderFacet): string {
  const d = fillablePlanViewPath(f.arcs1);
  // ViewBox from the ACTUAL arc geometry (the key coords are approximate):
  // sample each arc's swept points and take true bounds — never invent.
  const b = arcBounds(f.arcs1);
  if (!b) {
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 10"/>`;
  }
  const m = 2; // 2mm margin for the stroke
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${fmt(b.minX - m, 1)} ${fmt(b.minY - m, 1)} ${fmt(b.maxX - b.minX + 2 * m, 1)} ${fmt(b.maxY - b.minY + 2 * m, 1)}" preserveAspectRatio="xMidYMid meet">` +
    `<path d="${d}" fill="#3D9CC1" stroke="#111" stroke-width="0.8"/>` +
    `</svg>`;
}

/** Deterministic bounds over sampled arc points (5 samples per arc). */
function arcBounds(arcs: RenderArc[]): { minX: number; minY: number; maxX: number; maxY: number } | null {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  let any = false;
  for (const a of arcs) {
    // Sweep angles the same way arcToSvgPath does (verified semantics).
    let s0: number, e0: number, cx: number;
    if (a.d === 1) {
      cx = a.x;
      s0 = a.s;
      e0 = a.e < a.s ? a.e + 360 : a.e;
    } else {
      cx = -a.x;
      s0 = 180 - a.s;
      e0 = 180 - a.e;
    }
    for (let i = 0; i <= 5; i++) {
      const ang = s0 + ((e0 - s0) * i) / 5;
      const [x, y] = pointOnArc(cx, a.y, a.r, ang);
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
      any = true;
    }
  }
  return any ? { minX, minY, maxX, maxY } : null;
}

/**
 * Side-profile curve SVG from the facet's verified numbers only:
 * - tail at x=0, nose at x=lengthMm (facet.key orientation)
 * - circular tip/tail arcs of upturnRadius rising to estimatedHeightM
 *   (height = R - cos(lengthToContact/R)·R, computed upstream in D1)
 * - camber bow across the running length with Pete's mm mapping
 * Deterministic; qualitative only where the record carries no number.
 */
function sideProfileSvg(f: RenderFacet): string {
  const p = f.profile;
  const L = p.lengthMm;
  if (!Number.isFinite(L) || L <= 0) return '';
  const hN = p.noseHeightMm || 0;
  const hT = p.tailHeightMm || 0;
  const camber = p.camberHeightMm || 0;
  const lcT = Math.min(Math.max(p.tailLengthToContactMm || 0, 0), L / 2);
  const lcN = Math.min(Math.max(p.noseLengthToContactMm || 0, 0), L / 2);
  // CAMBER LIMITS (Pete 2026-10-26: "Y = 0 at the camber limits"): the camber
  // pocket runs exactly Tip Rocker Start to Tail Rocker Start — the bracket
  // tick stations (zone = rocker run + end-to-widest + signed Inward).
  //
  // EARLY RISE (Pete 2026-10-26, same thread): "Rocker is also called early
  // rise... the rise starts closer to the bindings than on a ski without
  // rocker where the rise starts where the tip/tail block rise begins. The
  // rocker description explains the slope of the upturn between the camber
  // limits and the block arc contact." From each limit (y=0) the surface
  // rises at the LABEL's slope toward the end ('30-10' -> 5mm over 300mm;
  // '12-10' -> 5mm over 120mm = 2.5x steeper) until the TRUE block arc (fixed
  // radius + end height) overtakes it; from that handoff the arc runs to the
  // end. The flat platforms previously drawn between limits and contacts are
  // superseded — that span IS the rocker's early rise. On a ski without
  // rocker the rise starts AT the block arc contact; that is also the
  // fallback when the label/widest inputs are missing.
  // Label decode self-check (tail, 230702): run 120 = 255.88 - 135.88 EXACT;
  // rise at widest = 5.0mm EXACT = label's 2nd number / 2.
  const parseRocker = (name: string): { runMm: number; riseMm: number; slope: number } | null => {
    const m = /^(\d+)-(\d+)$/.exec(name ?? '');
    if (!m) return null;
    const runMm = Number(m[1]) * 10;
    const riseMm = Number(m[2]) / 2; // 2nd number = 2x rise at the end's widest point
    return { runMm, riseMm, slope: runMm > 0 ? riseMm / runMm : 0 };
  };
  const tipRocker = parseRocker(p.tipRockerName);
  const tailRocker = parseRocker(p.tailRockerName);
  let tipZone = tipRocker && p.noseWidestFromEndMm !== undefined
    ? tipRocker.runMm + p.noseWidestFromEndMm + (p.noseInwardAdjustmentMm ?? 0) : NaN;
  let tailZone = tailRocker && p.tailWidestFromEndMm !== undefined
    ? tailRocker.runMm + p.tailWidestFromEndMm + (p.tailInwardAdjustmentMm ?? 0) : NaN;
  // ZERO/SHORT-RUN CLAMP (Pete 2026-10-27): "If Tip Rocker = 0, does the tip
  // upturn begin at the Tip Rise Start position?" — NO. At small/zero run the
  // raw zone (run + end-to-widest + Inward) lands INSIDE the region where the
  // block arc is already off the ground (230702: run 0 -> 116.77mm vs contact
  // 210.04, arc already 7mm high there). There is no y=0 that far out; the
  // camber pocket grounds at the block-arc CONTACT instead — Pete's own
  // no-rocker definition: "on a ski without rocker the rise starts where the
  // tip/tail block rise begins." Clamp each zone outward to its contact; the
  // early-rise blend then collapses (zone == contact -> no blend span) and
  // the arc starts lifting AT the contact, exactly the no-rocker picture.
  if (Number.isFinite(tipZone)) tipZone = Math.max(tipZone, lcN);
  if (Number.isFinite(tailZone)) tailZone = Math.max(tailZone, lcT);
  const useZones = Number.isFinite(tipZone) && Number.isFinite(tailZone)
    && tipZone > 0 && tailZone > 0 && tipZone + tailZone < L;
  // Frame discipline: zones are measured FROM EACH END; the SVG is drawn in
  // the tail-0 frame, so groundT = tailZone (x from tail) and
  // groundN = L - tipZone (x from tail = L - station from tip).
  const groundN = useZones ? L - tipZone : L - lcN;      // nose-side y=0 end (tail frame)
  const groundT = useZones ? tailZone : lcT;             // tail-side y=0 end (tail frame)
  // Screen height: max of all heights + 20% headroom, min 10.
  const H = Math.max(hN, hT, Math.abs(camber), 10) * 1.2;
  const y = (h: number) => H - h;
  const mid = (groundT + groundN) / 2;
  // Quadratic control point so the bow's apex lands exactly at camber height:
  // apex y = 0.5·y0 + 0.5·yC  =>  yC = 2·(H - camber) - H.
  // ALWAYS a Q bow (Pete 2026-10-24 per-camber display): even at 0 / negative
  // camber the bow must remain a sampled CURVE path so the PDF sideview's
  // baseline-vs-curve classification (baseline = the 2-pt M..L path; curve =
  // everything else) and the arc chaining stay stable across camber types.
  const yC = 2 * (H - camber) - H;
  const bow = `M ${fmt(groundT)} ${fmt(y(0))} Q ${fmt(mid)} ${fmt(yC)} ${fmt(groundN)} ${fmt(y(0))}`;
  // Early-rise handoff: the largest station-from-end s in [0, lc] where the
  // block arc still leads the rise line. arc(0)=h > line(0) guarantees a
  // crossing in (0, lc); line(lc) > 0 = arc(lc) guarantees it is a true
  // crossing. At the handoff both heights agree (continuous curve, real
  // transition kink — early-rise skis genuinely change slope there).
  const handoff = (limit: number, lc: number, R: number, slope: number): { s: number; h: number } => {
    const arcH = (s: number) => R - R * Math.cos((lc - s) / R);
    const lineH = (s: number) => slope * (limit - s);
    if (!(limit > lc) || !(slope > 0)) return { s: lc, h: 0 };  // no early rise: arc from contact
    if (arcH(0) - lineH(0) <= 0) return { s: 0, h: lineH(0) };  // line leads everywhere: no arc shows
    let lo = 0, hi = lc;
    for (let i = 0; i < 60; i++) {
      const midS = (lo + hi) / 2;
      if (arcH(midS) - lineH(midS) > 0) lo = midS; else hi = midS;
    }
    const s = (lo + hi) / 2;
    return { s, h: lineH(s) };
  };
  const tipHO = useZones && tipZone > lcN && tipRocker
    ? handoff(tipZone, lcN, p.noseRadiusMm || 1, tipRocker.slope) : { s: lcN, h: 0 };
  const tailHO = useZones && tailZone > lcT && tailRocker
    ? handoff(tailZone, lcT, p.tailRadiusMm || 1, tailRocker.slope) : { s: lcT, h: 0 };
  // SMOOTH BLEND (Pete 2026-10-26: "I don't want to see a kink... blend out
  // the kinks... that is how they turn out in reality. There is never a kink
  // look in the skis"): the early-rise span is drawn as a cubic Hermite that
  // is TANGENT to the bow at the camber limit (y=0 — the transition Pete
  // pinned) and TANGENT to the block arc at the handoff, so the whole
  // sideview is kink-free like a pressed ski. The straight label-slope line
  // is superseded for DRAWING (the label still defines run/rise numbers and
  // the zone stations; the handoff stays where the label line met the arc).
  // Bow end slopes (svg space): tangent of the Q at its ground endpoints —
  // the blend mirrors it for a rounded valley at y=0 (any camber sign; at 0
  // camber both slopes are 0 = perfectly flat joint).
  // Derived from the Q's own endpoint tangents:
  const qSlopeAtEnd = (xC: number, yC2: number, xE: number, yE: number) => (yE - yC2) / (xE - xC);
  const svgSlopeN = qSlopeAtEnd(mid, yC, groundN, y(0));   // dy_svg/dx at nose-side ground
  const svgSlopeT = qSlopeAtEnd(mid, yC, groundT, y(0));  // dy_svg/dx at tail-side ground (A-side: (yA-yC)/(xA-xC))
  const hSlopeN = -svgSlopeN;  // dh/dx (height) at groundN
  const hSlopeT = -svgSlopeT;  // dh/dx at groundT
  // Cubic BEZIER sampled as a polyline (24 pts so the PDF sampler keeps it
  // as a curve-class chain segment; a 2-pt M..L path would classify as a
  // baseline and drop out of the ski curve). Chosen over cubic Hermite: with
  // the arc's steep end tangent a Hermite OVERSHOOTS below y=0 mid-blend
  // (measured -2.97mm on 230702 — ski "buried" under the snow line). The
  // Bezier control points are clamped to heights >= 0 (hull property => the
  // curve can never dip) and to half the tangent run (keeps it monotone):
  //   start tangent m0, end tangent m1 (dh/dx, svg x frame)
  //   d_i = min(|span|/3, 0.5 * h_i / |m_i|)  (0 when m or h is 0)
  // so a flat start (m0=0 at the camber limit) gives a smooth valley-bottom
  // departure from the snow line, and the arc end lands tangent to the block
  // arc. C1 at the handoff; at the limit the only residual is the camber
  // bow's own endpoint slope (~0.01-0.02/mm display) — sub-visual, same
  // order as the reference sheet's own bow-to-flat junction.
  const bezierBlend = (
    x0: number, h0: number, m0: number,   // start: position + dh/dx
    x1: number, h1: number, m1: number,   // end: position + dh/dx
  ): string => {
    const span = x1 - x0;
    const sgn = Math.sign(span) || 1;
    // Tangent-preserving control distances. The first rule (h/|m| clamps) kept
    // the hull safe but ZEROED d1 when h1=0 (the camber-limit end), collapsing
    // the end tangent — measured end slope -0.0233 vs the bow's -0.0104 = a
    // residual kink exactly where Pete looks. Correct rule: clamp d only when
    // the control point would be pushed BELOW y=0, and then only to the exact
    // tangent-preserving value (control lands ON y=0, slope unchanged):
    //   c1h = h0 + m0·sgn·d0 < 0  <=>  m0·sgn < 0  -> d0 <= h0/(-m0·sgn)
    //   c2h = h1 - m1·sgn·d1 < 0  <=>  m1·sgn > 0  -> d1 <= h1/(m1·sgn)
    // At the limit end (h1=0) the control lands at |m1·sgn|·d1 >= 0 for any
    // d1, so the full span/3 tangent length is used -> TRUE C1 into the bow.
    const d0 = Math.min(Math.abs(span) / 3, m0 * sgn < -1e-9 ? h0 / (-m0 * sgn) : Infinity);
    const d1 = Math.min(Math.abs(span) / 3, m1 * sgn > 1e-9 ? h1 / (m1 * sgn) : Infinity);
    const c1x = x0 + sgn * d0, c1h = h0 + m0 * sgn * d0;
    const c2x = x1 - sgn * d1, c2h = h1 - m1 * sgn * d1;
    const N = 24;
    const pts: string[] = [];
    for (let k = 0; k <= N; k++) {
      const t = k / N, u = 1 - t;
      const h = u * u * u * h0 + 3 * u * u * t * c1h + 3 * u * t * t * c2h + t * t * t * h1;
      const x = u * u * u * x0 + 3 * u * u * t * c1x + 3 * u * t * t * c2x + t * t * t * x1;
      pts.push(`${k === 0 ? 'M' : 'L'} ${fmt(x)} ${fmt(y(h))}`);
    }
    return pts.join(' ');
  };
  // Tip blend: from the camber limit (groundN, h=0, mirrored bow slope) to
  // the handoff on the arc (L - tipHO.s, h=tipHO.h, arc's dh/dx there).
  // Arc dh/dx at station s from end: sin((lc - s)/R) (height rises with x
  // toward the nose end).
  const riseN = useZones && tipZone > lcN && tipHO.s < tipZone
    ? bezierBlend(
        L - tipHO.s, tipHO.h,
        Math.sin((lcN - tipHO.s) / (p.noseRadiusMm || 1)),
        groundN, 0, -hSlopeN,
      ) : '';
  // Tail blend: from the handoff on the arc (tailHO.s, h=tailHO.h) to the
  // camber limit (groundT, h=0, mirrored bow slope). Arc dh/dx at station x
  // from the tail end: -sin((lcT - x)/R).
  const riseT = useZones && tailZone > lcT && tailHO.s < tailZone
    ? bezierBlend(
        tailHO.s, tailHO.h, -Math.sin((lcT - tailHO.s) / (p.tailRadiusMm || 1)),
        groundT, 0, -hSlopeT,
      ) : '';
  // Baseline under the bow (the "ground" the camber gap shows against).
  const baseline = `M 0 ${fmt(y(0))} L ${fmt(L)} ${fmt(y(0))}`;
  // viewBox gains bottom room for the reverse dip (apex lands at H + |camber|).
  const dip = Math.max(0, -camber);
  const arcN = tipHO.s > 0
    ? `<path d="M ${fmt(L - tipHO.s)} ${fmt(y(tipHO.h))} A ${fmt(p.noseRadiusMm || 1)} ${fmt(p.noseRadiusMm || 1)} 0 0 1 ${fmt(L)} ${fmt(y(hN))}" fill="none" stroke="#111" stroke-width="1.4"/>`
    : '';
  const arcT = tailHO.s > 0
    ? `<path d="M ${fmt(tailHO.s)} ${fmt(y(tailHO.h))} A ${fmt(p.tailRadiusMm || 1)} ${fmt(p.tailRadiusMm || 1)} 0 0 0 ${fmt(0)} ${fmt(y(hT))}" fill="none" stroke="#111" stroke-width="1.4"/>`
    : '';
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${fmt(L, 0)} ${fmt(H + dip, 0)}" preserveAspectRatio="xMidYMid meet">` +
    `<path d="${baseline}" fill="none" stroke="#999" stroke-width="1" stroke-dasharray="4 3"/>` +
    `<path d="${bow}" fill="none" stroke="#111" stroke-width="1.4"/>` +
    (riseT ? `<path d="${riseT}" fill="none" stroke="#111" stroke-width="1.4"/>` : '') +
    (riseN ? `<path d="${riseN}" fill="none" stroke="#111" stroke-width="1.4"/>` : '') +
    arcN + arcT +
    `</svg>`;
}
